﻿/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { printStackTrace } from '@utils/printUtils';
import cloneDeep from 'lodash/cloneDeep';
import {
  SetStoreFunction,
  createStore as solidCreateStore,
} from 'solid-js/store';

// @ts-expect-error This is going to contain all of the different mobile stores.
window.mobileStore = !window.mobileStore ? {} : window.mobileStore;

type WindowStoreT = Record<
  string,
  SetStoreFunction<unknown> | (() => void) | object
>;

const stores: Array<{ reset: () => void }> = [];

/**
 * Create an instance of the app store. A unique name is required so that it's accessible through
 * `window.mobileStore[name]`. In the event you want to create multiple instances of a store, then you
 * can use `uniqueId` from lodash. The `overwrite` parameter could be set to `true`, but make sure
 * you are not overwriting an unrelated store.
 * @param initialState The initial/default value of the store.
 * @param name A unique name for this store. It can't match the name of any other store in the app.
 * @param overwrite In the event of a duplicate key, overwrite it. Use this only in the context of the same store.
 * @example
 * const [myStore, setMyStore, resetMyStore] = createAppStore<MyStoreT>(defaultVal, 'myStore');
 */
export function createAppStore<T extends object>(
  initialState: T,
  name: string,
  overwrite = false,
) {
  const initState = cloneDeep(initialState);
  const [store, setStore] = solidCreateStore<T>(initialState);

  if (!name) {
    throw new Error(
      `A unique store name is required: ${name ?? 'No name provided'}`,
    );

    // @ts-expect-error Check in the window.mobileStore object.
  } else if (name in (window.mobileStore as WindowStoreT) && !overwrite) {
    printStackTrace(`Store "${name}" already exists and will be overwritten`);
  }

  const reset = () => {
    setStore(cloneDeep(initState));
  };

  // @ts-expect-error Expose the value onto the window.
  (window.mobileStore as WindowStoreT)[name] = {
    store,
    setStore,
    reset,
  };

  stores.push({ reset });
  return [store, setStore, reset] as const;
}

export function resetAllStores() {
  stores.forEach((store) => {
    if (typeof store.reset === 'function') {
      store.reset();
    }
  });
}
